import type { Embed } from '@tunasong/models'
import type { EmbedSizes } from './types.js'

/** Example URLs
 *
 * <iframe width="100%" height="300" scrolling="no" frameborder="no" allow="autoplay" src="https://w.soundcloud.com/player/?url=https%3A//api.soundcloud.com/playlists/454981347&color=%23ff5500&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true&visual=true"></iframe><div style="font-size: 10px; color: #cccccc;line-break: anywhere;word-break: normal;overflow: hidden;white-space: nowrap;text-overflow: ellipsis; font-family: Interstate,Lucida Grande,Lucida Sans Unicode,Lucida Sans,Garuda,Verdana,Tahoma,sans-serif;font-weight: 100;"><a href="https://soundcloud.com/fresh-pressed" title="Fresh Pressed" target="_blank" style="color: #cccccc; text-decoration: none;">Fresh Pressed</a> · <a href="https://soundcloud.com/fresh-pressed/sets/fresh-pressed-tracks" title="Fresh Pressed Tracks" target="_blank" style="color: #cccccc; text-decoration: none;">Fresh Pressed Tracks</a></div>
 * https://soundcloud.com/autosonic/your-favorite-pastime#t=0:05
 * <iframe width="100%" height="300" scrolling="no" frameborder="no" allow="autoplay" src="https://w.soundcloud.com/player/?url=https%3A//api.soundcloud.com/tracks/377375357&color=%23ff5500&auto_play=true&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true&visual=true"></iframe><div style="font-size: 10px; color: #cccccc;line-break: anywhere;word-break: normal;overflow: hidden;white-space: nowrap;text-overflow: ellipsis; font-family: Interstate,Lucida Grande,Lucida Sans Unicode,Lucida Sans,Garuda,Verdana,Tahoma,sans-serif;font-weight: 100;"><a href="https://soundcloud.com/autosonic" title="Autosonic" target="_blank" style="color: #cccccc; text-decoration: none;">Autosonic</a> · <a href="https://soundcloud.com/autosonic/your-favorite-pastime" title="Your Favorite Pastime" target="_blank" style="color: #cccccc; text-decoration: none;">Your Favorite Pastime</a></div>
 */

/**
 * A public track (`/<user>/<track>`, optionally `/s-<secret>`) or playlist (`/<user>/sets/<playlist>`) link, with any
 * `?si=…` / `#t=…` tail. `on.soundcloud.com/<code>` short links are not matched: they only resolve via a redirect.
 */
const urlMatch =
  /^https:\/\/(?:www\.|m\.)?soundcloud\.com\/([\w-]+)\/((?:sets\/)?[\w-]+(?:\/s-[A-Za-z0-9]+)?)\/?(?:[?#].*)?$/
/** First path segments that are site pages, not users */
const reservedPaths = ['search', 'discover', 'you', 'stream', 'charts', 'upload', 'settings', 'messages', 'pages']
/** Second path segments that are profile tabs, not tracks */
const profileTabs = ['tracks', 'sets', 'albums', 'likes', 'reposts', 'popular-tracks', 'followers', 'following']
const iFrameMatch = /^\<iframe .* src=\"(https:\/\/w\.soundcloud\.com\/player\/[^"]*)\"/

const sizes: EmbedSizes = {
  small: {
    width: '50%',
    height: '80px',
  },
  medium: {
    width: '100%',
    height: 200,
  },
  large: {
    width: '100%',
    height: '300px',
  },
}
/** The official player takes the public URL directly, no API lookup needed (#1973) */
const playerUrl = (url: string) => {
  const [, user, path] = url.match(urlMatch) ?? []
  if (!user || !path || reservedPaths.includes(user) || profileTabs.includes(path)) {
    return null
  }
  return `https://w.soundcloud.com/player/?url=${encodeURIComponent(`https://soundcloud.com/${user}/${path}`)}`
}

export const parseSoundcloud = (urlOrUrn?: string): Embed | null => {
  if (!urlOrUrn) {
    return null
  }
  const trimmed = urlOrUrn.trim()
  const url = trimmed.match(iFrameMatch)?.[1] ?? playerUrl(trimmed)
  return url
    ? {
        type: 'embed',
        service: 'soundcloud',
        url,
        size: 'medium',
        children: [{ text: '' }],
      }
    : null
}

export default {
  parse: parseSoundcloud,
  sizes,
}
